/**
 * Stripe service — centralises all Stripe-related API calls.
 *
 * Covers: Connect account management, payment intents, checkout sessions,
 * refunds, order info, promo codes, and payment method updates.
 */
import api from './api'

// ---------------------------------------------------------------------------
// Stripe Connect
// ---------------------------------------------------------------------------

export function checkConnection() {
  return api.get('/stripe/checkConnection')
}

// Takes no client id: the platform's Connect client id is read server-side
// from STRIPE_CONNECT_CLIENT_ID. It used to be interpolated here from a
// caller-supplied literal, which is how a stale value reached Stripe and made
// Disconnect fail silently. Callers must catch -- a non-2xx rejects, and this
// endpoint answers 404 / 500 / 502 / 503. Full contract: billingService's
// disconnectStripeConnect, which is what the live surfaces call.
export function deauthorize() {
  return api.get('/stripe/connect/deauthorize')
}

export function getStripeAccountId() {
  return api.get('/stripe/getStripeAccountId')
}

export function getStripeAccountIdFromCampaign(campaignId) {
  return api.get(`/stripe/getStripeAccountIdFromCampaign/${campaignId}`)
}

// ---------------------------------------------------------------------------
// Payment
// ---------------------------------------------------------------------------

export function createPaymentIntent(data) {
  return api.post('/stripe/createPaymentIntent', data)
}

export function createPaymentIntentByAdmin(data) {
  return api.post('/stripe/createPaymentIntentByAdmin', data)
}

export function createStripeEvent(data) {
  return api.post('/stripe/createStripeEvent', data)
}

export function createCheckoutSession(subscriptionName) {
  return api.get(`/stripe/createCheckoutSession/${subscriptionName}`)
}

export function openCustomerPortal(customerId) {
  return api.get(`/stripe/openCustomerPortal/${customerId}`)
}

// ---------------------------------------------------------------------------
// Refunds
// ---------------------------------------------------------------------------

export function issueRefund(data) {
  return api.post('/stripe/issueRefund', data)
}

export function sendOrderRefundEmail(data) {
  return api.post('/stripe/sendOrderRefundEmail', data)
}

// ---------------------------------------------------------------------------
// Order Info
// ---------------------------------------------------------------------------

export function getStripeOrderInfo(data) {
  return api.post('/stripe/getStripeOrderInfo', data)
}

// ---------------------------------------------------------------------------
// Promo Codes & Payment Methods
// ---------------------------------------------------------------------------

export function checkPromoCode(code) {
  return api.post('/stripe/checkPromoCode/', { code })
}

export function updatePaymentMethod(data) {
  return api.post('/stripe/updatePaymentMethod/', data)
}

// ---------------------------------------------------------------------------
// Saved payment methods (account-scoped; backend PR #1001)
// ---------------------------------------------------------------------------

/**
 * Create a Stripe SetupIntent on the current shopper's customer.
 * Returns { client_secret, setup_intent_id }. The client_secret is consumed
 * by Stripe.js confirmCardSetup; backend ties it to the authenticated user.
 */
export function createSetupIntent() {
  return api.post('/stripe/createSetupIntent', {})
}

/**
 * List saved payment methods for the current shopper's Stripe customer.
 * Returns { payment_methods: [{id, brand, last4, exp_month, exp_year,
 * is_default}, ...] }. Account-scoped server-side via @tenant_scoped.
 */
export function listSavedPaymentMethods() {
  return api.post('/stripe/listPaymentMethods', {})
}

/**
 * Detach (delete) a payment method from the shopper's Stripe customer.
 * Returns { success: true } on success.
 */
export function detachPaymentMethod(paymentMethodId) {
  return api.post('/stripe/detachPaymentMethod', {
    payment_method_id: paymentMethodId,
  })
}

/**
 * Promote a saved payment method to the shopper's default. Takes ONLY the
 * payment_method_id — the backend resolves the caller's Stripe customer
 * server-side from the authenticated session. This intentionally avoids
 * round-tripping customer_id through the browser (PR #1003 gate review P0).
 *
 * Returns { success: true } on success. 404 if the PM doesn't belong to the
 * caller; 400 if payment_method_id is missing; 502 on upstream Stripe failure.
 */
export function setDefaultPaymentMethod(paymentMethodId) {
  return api.post('/stripe/setDefaultPaymentMethod', {
    payment_method_id: paymentMethodId,
  })
}

// ---------------------------------------------------------------------------
// The capability gate's refusal (PR #3364)
// ---------------------------------------------------------------------------

/**
 * The token the backend returns when the DISTRIBUTOR's Connect account
 * cannot take card payments. Kept as a constant because it is a contract
 * with `api/src/endpoints/stripe.py` -- if it changes there, the tests that
 * import this constant fail rather than the UI silently falling back to
 * showing the raw string.
 */
export const ACCOUNT_NOT_READY = 'stripe_account_not_ready'

/**
 * True when a rejected request is the capability gate's refusal.
 *
 * Guard-clause heavy on purpose. This runs inside a `catch`, and an error
 * REPORTER that throws masks the original failure -- the defect class
 * `store/notification.js` records as #2551/#2552. Every access here is
 * optional and the function returns a boolean for any input.
 */
export function isAccountNotReady(err) {
  return err?.response?.data?.error === ACCOUNT_NOT_READY
}

/**
 * The payment endpoints' refusal for an order that is already finalized,
 * placed or canceled (409 `order_not_payable`, #3992). Same defensive shape
 * as `isAccountNotReady`.
 */
export function isOrderNotPayable(err) {
  return err?.response?.data?.error === 'order_not_payable'
}

/**
 * What to show the shopper for that refusal. Usually the order was already
 * completed (another tab, a double submit), so it points them at their order
 * rather than asking them to re-enter a card.
 */
export function orderNotPayableMessage() {
  return (
    'This order has already been completed or closed, so it cannot be paid ' +
    'again. Check your order history or start a new order.'
  )
}

/**
 * What to show the SHOPPER when the store cannot take payment.
 *
 * Deliberately identical for every capability state. The distributor needs
 * to know whether they are `restricted` or `under_review`; a shopper does
 * not, and leaking it would publish the store's account standing to anyone
 * who can reach checkout.
 *
 * It also must not mention their card. Their card was never charged and is
 * not the problem -- telling them it was "declined" sends them off to
 * re-enter a card that would fail in exactly the same way.
 */
export function accountNotReadyMessage() {
  return (
    'This store cannot accept payment right now. ' +
    'Your card has not been charged, and your cart has been saved. ' +
    'Please try again later or contact the store.'
  )
}
